import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { format, formatDistanceToNow } from 'date-fns'
import {
  Send01Icon,
  PencilLineIcon,
  Trash01Icon,
  CornerUpLeftIcon,
  CheckCircleIcon,
  CircleIcon,
  EyeOffIcon,
  EyeIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  User01Icon,
  Users01Icon,
  AlertTriangleIcon,
} from '@untitledui-icons/react/line'
import { Button } from '@/components/base/buttons'
import { TextArea } from '@/components/base/textarea/textarea'
import { Badge } from '@/components/ui/Badge'
import { cn } from '@/lib/utils'
import { toast } from '@/lib/toast'
import {
  useConversationNotes,
  useCreateNote,
  useUpdateNote,
  useDeleteNote,
  useCreateReply,
} from '@/hooks/api/useConversationNotes'
import { useIdentity, usePermissions } from '@/hooks/api'
import { useAssignmentTargets } from '@/hooks/api/useUsers'
import { AlertDialog } from '@/components/ui/AlertDialog'
import { getErrorMessage } from '@/lib/api'
import type { Note, NoteReply, PriorityLevel, NoteStatus } from '@/types/conversation'

interface ConversationNotesProps {
  conversationId: string | number
  customerId?: number
  className?: string
}

// Assignment target type for users and teams
interface AssignmentTarget {
  id: number
  name: string
  type: 'user' | 'team'
}

interface MentionPayload {
  mentioned_user_ids: number[]
  mentioned_team_ids: number[]
}

const getMentionText = (target: AssignmentTarget) => `@${target.name}`

const getTargetKey = (target: AssignmentTarget) => `${target.type}-${target.id}`

const pruneMentionTargets = (content: string, targets: AssignmentTarget[]): AssignmentTarget[] =>
  targets.filter((target) => content.includes(getMentionText(target)))

const addMentionTarget = (
  targets: AssignmentTarget[],
  targetToAdd: AssignmentTarget
): AssignmentTarget[] => {
  const targetKey = getTargetKey(targetToAdd)
  if (targets.some((target) => getTargetKey(target) === targetKey)) {
    return targets
  }

  return [...targets, targetToAdd]
}

const getMentionPayload = (content: string, targets: AssignmentTarget[]): MentionPayload => {
  const activeTargets = pruneMentionTargets(content, targets)

  return {
    mentioned_user_ids: activeTargets
      .filter((target) => target.type === 'user')
      .map((target) => target.id),
    mentioned_team_ids: activeTargets
      .filter((target) => target.type === 'team')
      .map((target) => target.id),
  }
}

const getToastErrorMessage = (error: unknown, fallback: string): string => {
  const message = getErrorMessage(error)
  return message === 'An unexpected error occurred' ? fallback : message
}

const priorityColors: Record<PriorityLevel, string> = {
  low: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  medium: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
  critical: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
}

// Priority labels will be translated in the component

const priorityLevels: PriorityLevel[] = ['low', 'medium', 'high', 'critical']

// Dropdown Menu Component
function DropdownMenu({
  trigger,
  isOpen,
  onClose,
  children,
  className,
}: {
  trigger: React.ReactNode
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className="relative">
      {trigger}
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={onClose} />
          <div
            className={cn(
              'absolute top-full left-0 z-50 mt-1 max-h-[300px] min-w-[200px] overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800',
              className
            )}
          >
            {children}
          </div>
        </>
      )}
    </div>
  )
}

function MenuItem({
  children,
  onClick,
  selected,
  icon,
}: {
  children: React.ReactNode
  onClick: () => void
  selected?: boolean
  icon?: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700',
        selected && 'bg-brand-50 text-brand-700 dark:bg-brand-900/20 dark:text-brand-300'
      )}
    >
      {icon}
      {children}
    </button>
  )
}

function MenuHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase dark:text-gray-400">
      {children}
    </div>
  )
}

function MenuDivider() {
  return <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
}

function MentionsTextArea({
  value,
  onChange,
  placeholder,
  textAreaClassName,
  autoFocus,
  userTargets,
  teamTargets,
  selectedMentionTargets,
  onSelectedMentionTargetsChange,
}: {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  textAreaClassName?: string
  autoFocus?: boolean
  userTargets: AssignmentTarget[]
  teamTargets: AssignmentTarget[]
  selectedMentionTargets: AssignmentTarget[]
  onSelectedMentionTargetsChange: (targets: AssignmentTarget[]) => void
}) {
  const [mentionQuery, setMentionQuery] = useState('')
  const [mentionStartPos, setMentionStartPos] = useState<number | null>(null)
  const [showMentions, setShowMentions] = useState(false)
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0)

  const filteredMentions = showMentions
    ? [...userTargets, ...teamTargets].filter((target) =>
        target.name.toLowerCase().includes(mentionQuery)
      )
    : []

  const handleChange = (newValue: string) => {
    onChange(newValue)
    onSelectedMentionTargetsChange(pruneMentionTargets(newValue, selectedMentionTargets))

    const lastAtIndex = newValue.lastIndexOf('@')
    if (lastAtIndex !== -1) {
      const afterAt = newValue.slice(lastAtIndex + 1)
      if (!afterAt.includes(' ') && !afterAt.includes('\n')) {
        setMentionQuery(afterAt.toLowerCase())
        setMentionStartPos(lastAtIndex)
        setShowMentions(true)
        setSelectedMentionIndex(0)
        return
      }
    }

    setShowMentions(false)
    setMentionQuery('')
    setMentionStartPos(null)
    setSelectedMentionIndex(0)
  }

  const insertMention = (target: AssignmentTarget) => {
    if (mentionStartPos === null) return

    const before = value.slice(0, mentionStartPos)
    const after = value.slice(mentionStartPos + 1 + mentionQuery.length)
    const newContent = `${before}@${target.name} ${after}`

    onChange(newContent)
    onSelectedMentionTargetsChange(
      addMentionTarget(pruneMentionTargets(newContent, selectedMentionTargets), target)
    )
    setShowMentions(false)
    setMentionQuery('')
    setMentionStartPos(null)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showMentions || filteredMentions.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedMentionIndex((prev) => (prev < filteredMentions.length - 1 ? prev + 1 : prev))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedMentionIndex((prev) => (prev > 0 ? prev - 1 : 0))
        break
      case 'Enter': {
        const target = filteredMentions[selectedMentionIndex]
        if (!target) return
        e.preventDefault()
        insertMention(target)
        break
      }
      case 'Escape':
        e.preventDefault()
        setShowMentions(false)
        setMentionQuery('')
        setMentionStartPos(null)
        setSelectedMentionIndex(0)
        break
    }
  }

  return (
    <div className="relative" onKeyDown={handleKeyDown}>
      <TextArea
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        textAreaClassName={textAreaClassName}
        autoFocus={autoFocus}
      />

      {showMentions && filteredMentions.length > 0 && (
        <div className="absolute top-full left-0 z-50 mt-1 max-h-[200px] w-64 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
          {filteredMentions.map((target, index) => (
            <button
              key={`${target.type}-${target.id}`}
              onClick={() => insertMention(target)}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700',
                index === selectedMentionIndex && 'bg-gray-100 dark:bg-gray-700'
              )}
            >
              {target.type === 'user' ? (
                <User01Icon className="h-4 w-4 text-gray-400" />
              ) : (
                <Users01Icon className="h-4 w-4 text-gray-400" />
              )}
              <span>{target.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function NoteCard({
  note,
  currentUserId,
  userTargets,
  teamTargets,
  onToggleStatus,
  onEdit,
  onDelete,
  onReply,
}: {
  note: Note
  currentUserId?: number
  userTargets: AssignmentTarget[]
  teamTargets: AssignmentTarget[]
  onToggleStatus: (note: Note) => void
  onEdit: (note: Note) => void
  onDelete: (note: Note) => void
  onReply: (
    noteId: string,
    content: string,
    closeNote: boolean,
    mentionPayload: MentionPayload
  ) => void
}) {
  const { t } = useTranslation()
  const [isReplying, setIsReplying] = useState(false)
  const [replyContent, setReplyContent] = useState('')
  const [replyMentionTargets, setReplyMentionTargets] = useState<AssignmentTarget[]>([])
  const [isExpanded, setIsExpanded] = useState(false)
  const isOwner = currentUserId === note.created_by
  const createdByName = note.created_by_user
    ? `${note.created_by_user.first_name} ${note.created_by_user.last_name}`
    : t('resources.conversations.user_fallback', { id: note.created_by })

  const handleSubmitReply = (closeNote: boolean = false) => {
    if (replyContent.trim()) {
      onReply(
        note.id,
        replyContent,
        closeNote,
        getMentionPayload(replyContent, replyMentionTargets)
      )
      setReplyContent('')
      setReplyMentionTargets([])
      setIsReplying(false)
      setIsExpanded(true)
    }
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
      {/* Header with actions */}
      <div className="mb-2 flex items-start justify-between">
        <div className="flex items-center gap-2">
          <div className="bg-brand-100 text-brand-700 dark:bg-brand-900 dark:text-brand-300 flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium">
            {createdByName?.charAt(0) || '?'}
          </div>
          <div>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {createdByName}
            </span>
            <p className="text-xs text-gray-500">
              {formatDistanceToNow(new Date(note.created_at), { addSuffix: true })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            color="tertiary"
            size="sm"
            onPress={() => onToggleStatus(note)}
            className={cn(
              'h-8 w-8 p-0',
              note.status === 'closed' ? 'text-green-600' : 'text-gray-500'
            )}
            iconLeading={note.status === 'closed' ? CheckCircleIcon : CircleIcon}
          />
          {isOwner && (
            <>
              <Button
                color="tertiary"
                size="sm"
                onPress={() => onEdit(note)}
                className="h-8 w-8 p-0 text-gray-500"
                iconLeading={PencilLineIcon}
              />
              <Button
                color="tertiary"
                size="sm"
                onPress={() => onDelete(note)}
                className="h-8 w-8 p-0 text-gray-500 hover:text-red-600"
                iconLeading={Trash01Icon}
              />
            </>
          )}
        </div>
      </div>

      {/* Note content */}
      <p className="mb-3 text-sm whitespace-pre-line text-gray-700 dark:text-gray-300">
        {note.content}
      </p>

      {/* Metadata badges */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {note.priority && (
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-xs font-medium capitalize',
              priorityColors[note.priority]
            )}
          >
            {t(`resources.conversation_notes.priority.${note.priority}`)}
          </span>
        )}
        {note.status === 'open' && (
          <Badge variant="success" size="sm">
            {t('resources.conversation_notes.status.open')}
          </Badge>
        )}
        {note.is_private && (
          <span className="text-brand-600 flex items-center gap-1 text-xs">
            <EyeOffIcon className="h-3 w-3" />
            {t('resources.conversation_notes.team_only')}
          </span>
        )}
      </div>

      {/* Replies section */}
      {note.replies && note.replies.length > 0 && (
        <>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex w-full items-center justify-between border-t border-gray-100 pt-2 text-sm text-gray-600 hover:text-gray-900 dark:border-gray-700 dark:text-gray-400"
          >
            <span>{t('resources.conversation_notes.replies', { count: note.replies.length })}</span>
            {isExpanded ? (
              <ChevronUpIcon className="h-4 w-4" />
            ) : (
              <ChevronDownIcon className="h-4 w-4" />
            )}
          </button>

          {isExpanded && (
            <div className="mt-3 space-y-3 border-l-2 border-gray-200 pl-4 dark:border-gray-600">
              {note.replies.map((reply: NoteReply) => {
                const replyCreatedByName = reply.created_by_user
                  ? `${reply.created_by_user.first_name} ${reply.created_by_user.last_name}`
                  : t('resources.conversations.user_fallback', { id: reply.created_by })
                return (
                  <div key={reply.id} className="text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-700 dark:text-gray-300">
                        {replyCreatedByName}
                      </span>
                      <span className="text-xs text-gray-500">
                        {format(new Date(reply.created_at), 'MMM d, h:mm a')}
                      </span>
                      {reply.is_private && <EyeOffIcon className="text-brand-500 h-3 w-3" />}
                    </div>
                    <p className="mt-1 whitespace-pre-line text-gray-600 dark:text-gray-400">
                      {reply.content}
                    </p>
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}

      {/* Reply action */}
      <div className="mt-2 border-t border-gray-100 pt-2 dark:border-gray-700">
        {isReplying ? (
          <div className="space-y-2">
            <MentionsTextArea
              value={replyContent}
              onChange={setReplyContent}
              placeholder={t('resources.conversation_notes.write_reply')}
              textAreaClassName="min-h-[60px] text-sm"
              autoFocus
              userTargets={userTargets}
              teamTargets={teamTargets}
              selectedMentionTargets={replyMentionTargets}
              onSelectedMentionTargetsChange={setReplyMentionTargets}
            />
            <div className="flex justify-end gap-2">
              <Button
                color="secondary"
                size="sm"
                onPress={() => {
                  setIsReplying(false)
                  setReplyContent('')
                  setReplyMentionTargets([])
                }}
              >
                {t('resources.generic.cancel')}
              </Button>
              <Button
                color="primary"
                size="sm"
                onPress={() => handleSubmitReply(false)}
                isDisabled={!replyContent.trim()}
                iconLeading={Send01Icon}
              >
                {t('resources.conversation_notes.reply')}
              </Button>
              {note.status !== 'closed' && (
                <Button
                  color="primary"
                  size="sm"
                  onPress={() => handleSubmitReply(true)}
                  isDisabled={!replyContent.trim()}
                  iconLeading={CheckCircleIcon}
                >
                  {t('resources.conversation_notes.actions.reply_and_close')}
                </Button>
              )}
            </div>
          </div>
        ) : (
          <button
            onClick={() => setIsReplying(true)}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <CornerUpLeftIcon className="h-4 w-4" />
            {t('resources.conversation_notes.reply')}
          </button>
        )}
      </div>
    </div>
  )
}

export function ConversationNotes({
  conversationId,
  customerId,
  className,
}: ConversationNotesProps) {
  const { t } = useTranslation()
  const [showNewNote, setShowNewNote] = useState(false)
  const [newNoteContent, setNewNoteContent] = useState('')
  const [newNoteIsPrivate, setNewNoteIsPrivate] = useState(false)
  const [newNoteStatus, setNewNoteStatus] = useState<NoteStatus>('open')
  const [newNotePriority, setNewNotePriority] = useState<PriorityLevel | null>(null)
  const [newNoteAssignedTo, setNewNoteAssignedTo] = useState<AssignmentTarget | null>(null)
  const [newNoteMentionTargets, setNewNoteMentionTargets] = useState<AssignmentTarget[]>([])
  const [deleteNote, setDeleteNote] = useState<Note | null>(null)

  // Dropdown states
  const [assignMenuOpen, setAssignMenuOpen] = useState(false)
  const [priorityMenuOpen, setPriorityMenuOpen] = useState(false)

  const { data: identity } = useIdentity()
  const permissions = usePermissions()
  const { data: notes = [], isLoading } = useConversationNotes(conversationId)
  const createNote = useCreateNote()
  const updateNote = useUpdateNote()
  const deleteNoteMutation = useDeleteNote()
  const createReply = useCreateReply()

  // Translated priority labels
  const priorityLabels: Record<PriorityLevel, string> = {
    low: t('resources.conversation_notes.priority.low'),
    medium: t('resources.conversation_notes.priority.medium'),
    high: t('resources.conversation_notes.priority.high'),
    critical: t('resources.conversation_notes.priority.critical'),
  }

  // Fetch users and teams for assignment dropdown, filtered by customer
  const { userTargets, teamTargets } = useAssignmentTargets(customerId)

  // Check if user can toggle privacy (partner or admin)
  const canTogglePrivacy = permissions.canManage

  const handleAddNote = () => {
    if (!newNoteContent.trim()) return

    const noteData: {
      conversation_id: string | number
      content: string
      status: NoteStatus
      is_private: boolean
      priority: PriorityLevel | null
      assigned_to: number | null
      assigned_team_id: number | null
      mentioned_user_ids: number[]
      mentioned_team_ids: number[]
    } = {
      conversation_id: conversationId,
      content: newNoteContent,
      status: newNoteStatus,
      is_private: newNoteIsPrivate,
      priority: newNotePriority,
      assigned_to: null,
      assigned_team_id: null,
      ...getMentionPayload(newNoteContent, newNoteMentionTargets),
    }

    // Handle assignment
    if (newNoteAssignedTo) {
      if (newNoteAssignedTo.type === 'user') {
        noteData.assigned_to = newNoteAssignedTo.id
      } else if (newNoteAssignedTo.type === 'team') {
        noteData.assigned_team_id = newNoteAssignedTo.id
      }
    }

    createNote.mutate(noteData, {
      onSuccess: () => {
        toast.success(t('resources.conversation_notes.added'))
        handleCancelNewNote()
      },
      onError: (error: unknown) => {
        toast.error(getToastErrorMessage(error, t('resources.conversation_notes.add_failed')))
      },
    })
  }

  const handleCancelNewNote = () => {
    setNewNoteContent('')
    setNewNoteIsPrivate(false)
    setNewNoteStatus('open')
    setNewNotePriority(null)
    setNewNoteAssignedTo(null)
    setNewNoteMentionTargets([])
    setShowNewNote(false)
  }

  const handleToggleStatus = (note: Note) => {
    updateNote.mutate({
      id: note.id,
      status: note.status === 'closed' ? 'open' : 'closed',
    })
  }

  const handleDeleteConfirm = () => {
    if (!deleteNote) return
    deleteNoteMutation.mutate(deleteNote.id, {
      onSuccess: () => {
        toast.success(t('resources.conversation_notes.deleted'))
        setDeleteNote(null)
      },
      onError: (error: unknown) => {
        toast.error(getToastErrorMessage(error, t('resources.conversation_notes.delete_failed')))
      },
    })
  }

  const handleReply = (
    noteId: string,
    content: string,
    closeNote: boolean,
    mentionPayload: MentionPayload
  ) => {
    createReply.mutate(
      { noteId, content, close_note: closeNote, ...mentionPayload },
      {
        onSuccess: () => {
          toast.success(t('resources.conversation_notes.reply_added'))
        },
        onError: () => {
          toast.error(t('resources.conversation_notes.reply_failed'))
        },
      }
    )
  }

  const handleAssign = (target: AssignmentTarget | null) => {
    setNewNoteAssignedTo(target)
    setAssignMenuOpen(false)
  }

  const handlePrioritySelect = (priority: PriorityLevel | null) => {
    setNewNotePriority(priority)
    setPriorityMenuOpen(false)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="border-brand-500 h-6 w-6 animate-spin rounded-full border-2 border-t-transparent" />
        <span className="sr-only">{t('resources.generic.loading')}</span>
      </div>
    )
  }

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('resources.conversations.tabs.notes')}
        </h3>
        {!showNewNote && (
          <Button
            color="secondary"
            size="sm"
            onPress={() => setShowNewNote(true)}
            iconLeading={Send01Icon}
          >
            {t('resources.conversation_notes.new_note')}
          </Button>
        )}
      </div>

      {/* Notes list - show above the form */}
      <div className="space-y-3">
        {notes.length > 0 ? (
          notes.map((note) => (
            <NoteCard
              key={note.id}
              note={note}
              currentUserId={identity?.id}
              userTargets={userTargets}
              teamTargets={teamTargets}
              onToggleStatus={handleToggleStatus}
              onEdit={() => {
                toast.info(t('resources.conversation_notes.edit_coming_soon'))
              }}
              onDelete={setDeleteNote}
              onReply={handleReply}
            />
          ))
        ) : (
          <p className="py-8 text-center text-gray-500">
            {t('resources.conversation_notes.empty')}
          </p>
        )}
      </div>

      {/* New note form - always at the bottom */}
      {showNewNote && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <MentionsTextArea
            value={newNoteContent}
            onChange={setNewNoteContent}
            placeholder={t('resources.conversation_notes.add_placeholder')}
            textAreaClassName="min-h-[80px]"
            autoFocus
            userTargets={userTargets}
            teamTargets={teamTargets}
            selectedMentionTargets={newNoteMentionTargets}
            onSelectedMentionTargetsChange={setNewNoteMentionTargets}
          />

          {/* Controls row */}
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              {/* Assign To dropdown */}
              <DropdownMenu
                isOpen={assignMenuOpen}
                onClose={() => setAssignMenuOpen(false)}
                trigger={
                  <Button
                    color="secondary"
                    size="sm"
                    onPress={() => setAssignMenuOpen(!assignMenuOpen)}
                    className="text-gray-600"
                    iconLeading={User01Icon}
                  >
                    {newNoteAssignedTo
                      ? newNoteAssignedTo.name
                      : t('resources.conversation_notes.assign_to')}
                  </Button>
                }
              >
                {userTargets.length > 0 && (
                  <>
                    <MenuHeader>{t('resources.conversation_notes.users')}</MenuHeader>
                    {userTargets.map((target) => (
                      <MenuItem
                        key={`user-${target.id}`}
                        onClick={() => handleAssign(target)}
                        selected={
                          newNoteAssignedTo?.id === target.id && newNoteAssignedTo?.type === 'user'
                        }
                        icon={<User01Icon className="h-4 w-4 text-gray-400" />}
                      >
                        {target.name}
                      </MenuItem>
                    ))}
                  </>
                )}

                {teamTargets.length > 0 && (
                  <>
                    <MenuDivider />
                    <MenuHeader>{t('resources.conversation_notes.teams')}</MenuHeader>
                    {teamTargets.map((target) => (
                      <MenuItem
                        key={`team-${target.id}`}
                        onClick={() => handleAssign(target)}
                        selected={
                          newNoteAssignedTo?.id === target.id && newNoteAssignedTo?.type === 'team'
                        }
                        icon={<Users01Icon className="h-4 w-4 text-gray-400" />}
                      >
                        {target.name}
                      </MenuItem>
                    ))}
                  </>
                )}

                {userTargets.length === 0 && teamTargets.length === 0 && (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    {t('resources.conversation_notes.no_assignees')}
                  </div>
                )}
              </DropdownMenu>

              {/* Priority dropdown */}
              <DropdownMenu
                isOpen={priorityMenuOpen}
                onClose={() => setPriorityMenuOpen(false)}
                trigger={
                  <Button
                    color="secondary"
                    size="sm"
                    onPress={() => setPriorityMenuOpen(!priorityMenuOpen)}
                    className="text-gray-600"
                    iconLeading={AlertTriangleIcon}
                  >
                    {newNotePriority
                      ? priorityLabels[newNotePriority]
                      : t('resources.conversation_notes.priority.label')}
                  </Button>
                }
              >
                <MenuItem onClick={() => handlePrioritySelect(null)} selected={!newNotePriority}>
                  {t('resources.conversation_notes.priority.default')}
                </MenuItem>
                <MenuDivider />
                {priorityLevels.map((level) => (
                  <MenuItem
                    key={level}
                    onClick={() => handlePrioritySelect(level)}
                    selected={newNotePriority === level}
                  >
                    <span className={cn('capitalize', priorityColors[level].split(' ')[1])}>
                      {priorityLabels[level]}
                    </span>
                  </MenuItem>
                ))}
              </DropdownMenu>

              {/* Public/Private toggle */}
              {canTogglePrivacy && (
                <Button
                  color="tertiary"
                  size="sm"
                  onPress={() => setNewNoteIsPrivate(!newNoteIsPrivate)}
                  className={cn('text-gray-600', newNoteIsPrivate && 'text-brand-600')}
                  iconLeading={newNoteIsPrivate ? EyeOffIcon : EyeIcon}
                >
                  {newNoteIsPrivate
                    ? t('resources.conversation_notes.team_only')
                    : t('resources.conversation_notes.public')}
                </Button>
              )}

              {/* Open/Closed status toggle */}
              <Button
                color="tertiary"
                size="sm"
                onPress={() => setNewNoteStatus(newNoteStatus === 'open' ? 'closed' : 'open')}
                className={cn('text-gray-600', newNoteStatus === 'closed' && 'text-green-600')}
                title={
                  newNoteStatus === 'closed'
                    ? t('resources.conversation_notes.status.closed_hint')
                    : t('resources.conversation_notes.status.open_hint')
                }
                iconLeading={newNoteStatus === 'closed' ? CheckCircleIcon : CircleIcon}
              >
                {newNoteStatus === 'closed'
                  ? t('resources.conversation_notes.status.closed')
                  : t('resources.conversation_notes.status.open')}
              </Button>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2">
              <Button color="tertiary" size="sm" onPress={handleCancelNewNote}>
                {t('resources.generic.cancel')}
              </Button>
              <Button
                color="primary"
                size="sm"
                onPress={handleAddNote}
                isDisabled={!newNoteContent.trim() || createNote.isPending}
                iconLeading={Send01Icon}
              >
                {t('resources.conversation_notes.add_note')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      <AlertDialog
        isOpen={!!deleteNote}
        onClose={() => setDeleteNote(null)}
        onConfirm={handleDeleteConfirm}
        title={t('resources.conversation_notes.delete_title')}
        description={t('resources.conversation_notes.delete_description')}
        confirmLabel={t('resources.generic.delete')}
        variant="error"
      />
    </div>
  )
}

export default ConversationNotes
